import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Button,
  Input,
  Select,
  FormControl,
  FormLabel,
  useToast,
  Grid,
  GridItem,
  Textarea,
  Checkbox,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import axiosInstance from "../../../config/axiosInstance";

const ContactDrawer1 = ({ productId, isOpen, onClose }) => {
  const toast = useToast();
  const btnRef = useRef();

  const [form, setForm] = useState({
    title: "",
    name: "",
    email: "",
    phone: "",
    country: "",
    message: "",
    agreedToTerms: false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validateForm = () => {
    const nameRegex = /^[A-Za-z\s]{2,}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;

    if (!form.title.trim()) {
      toast({ title: "Title is required", status: "error", duration: 4000 });
      return false;
    }
    if (!nameRegex.test(form.name)) {
      toast({
        title: "Invalid Name",
        description: "Enter a valid name (only letters, min 2 characters).",
        status: "error",
        duration: 4000,
      });
      return false;
    }
    if (!emailRegex.test(form.email)) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        status: "error",
        duration: 4000,
      });
      return false;
    }
    if (form.phone && !phoneRegex.test(form.phone)) {
      toast({
        title: "Invalid Phone Number",
        description: "Phone must be 10 digits or leave blank.",
        status: "error",
        duration: 4000,
      });
      return false;
    }
    if (!form.country) {
      toast({ title: "Country is required", status: "error", duration: 4000 });
      return false;
    }
    if (!form.message.trim()) {
      toast({
        title: "Message is required",
        status: "error",
        duration: 4000,
      });
      return false;
    }
    if (!form.agreedToTerms) {
      toast({
        title: "Agreement Required",
        description: "You must agree to the terms and conditions.",
        status: "warning",
        duration: 4000,
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const payload = { ...form, productId };
      await axiosInstance.post("/api/high-jewellery/inquiry", payload);

      toast({
        title: "Inquiry Submitted.",
        description: "Your request has been sent.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });

      onClose();
      setForm({
        title: "",
        name: "",
        email: "",
        phone: "",
        country: "",
        message: "",
        agreedToTerms: false,
      });
    } catch (err) {
      if (err.response?.status === 401) {
        toast({
          title: "Unauthorized",
          description: "You must be logged in to book an appointment.",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        setTimeout(() => {
          window.location.href = "/login";
        }, 1000);
      } else {
        toast({
          title: "Error",
          description: "Something went wrong. Please try again later.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      finalFocusRef={btnRef}
      size="xl"
    >
      <DrawerOverlay />
      <DrawerContent maxW="1000px">
        <DrawerCloseButton />
        <DrawerHeader fontSize="3xl">Contact Expert</DrawerHeader>

        <DrawerBody>
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
            gap={4}
          >
            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="Mr, Mrs, Ms, etc."
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Name</FormLabel>
              <Input
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Enter your full name"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Enter your email"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Phone</FormLabel>
              <Input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="Enter your phone number"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Country/Region</FormLabel>
              <Select
                name="country"
                value={form.country}
                onChange={handleChange}
              >
                <option value="">Select your country</option>
                <option value="India">India</option>
                <option value="USA">USA</option>
                <option value="United Arab Emirates">United Arab Emirates</option>
              </Select>
            </FormControl>

            <GridItem colSpan={2}>
              <FormControl isRequired>
                <FormLabel>Message</FormLabel>
                <Textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Enter your message"
                  rows={4}
                />
              </FormControl>
            </GridItem>

            <GridItem colSpan={2}>
              <FormControl>
                <Checkbox
                  name="agreedToTerms"
                  isChecked={form.agreedToTerms}
                  onChange={handleChange}
                >
                  I agree to the terms and conditions
                </Checkbox>
              </FormControl>
            </GridItem>
          </Grid>
        </DrawerBody>

        <DrawerFooter>
          <Button
            backgroundColor="black"
            color="white"
            _hover={{ bg: "gray.700" }}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default ContactDrawer1;
